import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Pagination, Table } from "flowbite-react";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { ADMIN, CATEGORIES } from "../../config/apiConfig";
import AutoCompleteInput from "../inputs/Autocomplete";
import EmptyTable from "./EmptyTable";
import DrawerLoading from "../DrawerLoading";
import { useReactToPrint } from "react-to-print";
import TitleStripe from "../common/TitleStripe";
import { LuFilter, LuFilterX } from "react-icons/lu";
import { useApplication } from "../../contexts/ApplicationContext";
import ReactApexChart from "react-apexcharts";

const AdminLogErrorsProviders = ({ isActive = true }) => {
  const { userPermission } = useApplication();
  const [activeTab, setActiveTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const onPageChange = (page) => setCurrentPage(page);
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);

  useEffect(() => {
    if (showFilters) {
      setRenderFilters(true);
    } else {
      const timeout = setTimeout(() => setRenderFilters(false), 500);
      return () => clearTimeout(timeout);
    }
  }, [showFilters]);

  const [selectedProvider, setSelectedProvider] = useState({
    label: "Todos os provedores",
    value: "",
  });
  const [selectedProduct, setSelectedProduct] = useState({
    label: "Todos os produtos",
    value: "",
  });

  const [startDate, setStartDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );

  const reportRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef: reportRef });

  const {
    isLoading: isLoadingQueries,
    error: errorQueries,
    data: listQueries,
  } = useQuery(
    ["listQueries"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_QUERIES}?itemsPerPage=-1&isModular=1`
      );
      if (!data?.data?.data) throw new Error("Erro ao buscar insumos");
      let customers = data.data.data.map((customer) => ({
        label: customer.name,
        value: customer.id,
      }));
      customers = [{ label: "Todos os insumos", value: 0 }, ...customers];
      return customers;
    },
    { enabled: isActive }
  );

  const {
    isLoading: isLoadingProviders,
    error: errorListProviders,
    data: listProviders,
  } = useQuery(
    ["listProviders"],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_PROVIDERS}`);
      if (!data?.data) throw new Error("Erro ao buscar provedores");
      let providers = data?.data?.map((provider) => ({
        label: provider.name,
        value: provider.id,
      }));
      providers = [{ label: "Todos os provedores", value: "" }, ...providers];
      return providers;
    },
    { enabled: isActive }
  );

  const { isLoading: isLoadingCategories, error: errorCategories } = useQuery(
    ["categories", currentPage],
    async () => {
      const { data } = await axios.get(`${CATEGORIES.GET_ALL}`);
      return data.data.data;
    }
  );

  const {
    isLoading: isLoadingReportSimplifiedQueries,
    error: errorListReportSimplifiedQueries,
    data: listReportSimplifiedQueries,
  } = useQuery(
    [
      "listReportSimplifiedQueries",
      startDate,
      endDate,
      selectedProvider,
      selectedProduct,
    ],
    async () => {
      const { data } = await axios.get(
        `${
          ADMIN.GET_FAILED_MODULAR_QUERY
        }?startDate=${startDate}&endDate=${endDate}&providerId=${
          selectedProvider?.value || ""
        }&queries=${selectedProduct?.value || ""}`
      );
      return data.data.data;
    },
    {
      enabled: activeTab === 1 && isActive,
      refetchInterval: 40000,
    }
  );

  // ---------- Aggregação para o gráfico: falhas por provedor ----------
  const providerCounts = useMemo(() => {
    if (
      !listReportSimplifiedQueries ||
      listReportSimplifiedQueries?.length === 0
    )
      return [];
    const map = new Map();
    for (const item of listReportSimplifiedQueries) {
      const key = item.providerName || "—";
      map.set(key, (map.get(key) || 0) + 1);
    }
    // Ordena do maior para o menor
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [listReportSimplifiedQueries]);

  const chartCategories = useMemo(
    () => providerCounts.map(([name]) => name),
    [providerCounts]
  );
  const chartSeries = useMemo(
    () => [{ name: "Falhas", data: providerCounts.map(([, count]) => count) }],
    [providerCounts]
  );

  // Largura dinâmica para evitar labels espremidos; com overflow-x no wrapper
  const chartWidth = Math.max(700, chartCategories.length * 90);

  const chartOptions = useMemo(
    () => ({
      chart: {
        id: "falhas-por-provedor",
        toolbar: { show: false },
        animations: { enabled: true },
      },
      colors: ["#ef4444"], // vermelho (tailwind red-500)
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "45%",
          borderRadius: 6,
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val}`,
        style: { fontSize: "11px" },
      },
      xaxis: {
        categories: chartCategories,
        labels: { rotate: -45, trim: true, style: { fontSize: "11px" } },
      },
      yaxis: {
        title: { text: "Erros" },
        labels: { style: { fontSize: "11px" } },
      },
      tooltip: {
        y: { formatter: (val) => `${val} erro${val === 1 ? "" : "s"}` },
      },
      grid: { strokeDashArray: 4 },
    }),
    [chartCategories]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const isProviderFiltered = Boolean(selectedProvider?.value);

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => setShowFilters((prev) => !prev)}
          className="primary text-white transition-all duration-300"
        >
          <div className="flex items-center gap-2 transition-all duration-300">
            <span>{showFilters ? "Esconder filtros" : "Mostrar filtros"}</span>
            <span className="transition-transform duration-300">
              {!showFilters ? <LuFilter /> : <LuFilterX />}
            </span>
          </div>
        </Button>
      </div>

      <div className="flex flex-col gap-4">
        {showFilters && (
          <div className="transition-all duration-500 ease-in-out overflow-hidden bg-white rounded-md shadow-sm border border-gray-200 p-4 relative">
            {renderFilters && (
              <div className="transition-opacity duration-500 min-w-[200px]">
                {(isLoadingCategories || isLoadingProviders) && (
                  <DrawerLoading />
                )}
                <div className="title-syncx">Filtro avançado</div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
                  <div className="mt-4 min-w-[250px]">
                    <AutoCompleteInput
                      options={listProviders}
                      label="Provedor"
                      onChange={(provider) => setSelectedProvider(provider)}
                      value={selectedProvider?.value}
                      isLoading={isLoadingProviders}
                    />
                    {errorListProviders && (
                      <Alert color="failure" className="mt-2">
                        Ocorreu um erro ao buscar provedores
                      </Alert>
                    )}
                  </div>

                  <div className="mt-4 min-w=[250px]">
                    <AutoCompleteInput
                      options={listQueries}
                      label="Insumo"
                      onChange={(product) => setSelectedProduct(product)}
                      value={selectedProduct?.value}
                      isLoading={isLoadingQueries}
                    />
                    {errorQueries && (
                      <Alert color="failure" className="mt-2">
                        Ocorreu um erro ao buscar insumos
                      </Alert>
                    )}
                  </div>

                  <div>
                    <div className="font-semibold mt-1 text-sm mb-2">
                      Período
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate))) {
                            setEndDate(e.target.value);
                          }
                        }}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 focus:outline-none"
                      />
                      <div className="text-xs">Até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div
          className="flex-1 overflow-x-scroll print-padding print-content"
          ref={reportRef}
        >
          <TitleStripe>Provedores com Falha (Insumos)</TitleStripe>
          <div className="italic text-sm flex items-center gap-2 mt-3">
            Informações baseadas no período de{" "}
            <Badge className="w-fit">
              {moment(startDate).format("DD/MM/yyyy")}
            </Badge>{" "}
            até{" "}
            <Badge className="w-fit">
              {moment(endDate).format("DD/MM/yyyy")}
            </Badge>
          </div>
          {/* ---------- Gráfico: Falhas por Provedor ---------- */}
          {/* ---------- Gráfico: Falhas por Provedor ---------- */}
          {/* ---------- Gráfico: Falhas por Provedor ---------- */}
          {isProviderFiltered ? null : isLoadingReportSimplifiedQueries ? (
            <div className="relative h-40 mt-4">
              <DrawerLoading />
            </div>
          ) : errorListReportSimplifiedQueries ? (
            <Alert color="failure" className="mt-4">
              Ocorreu um erro ao carregar o gráfico.
            </Alert>
          ) : !listReportSimplifiedQueries ||
            listReportSimplifiedQueries.length === 0 ? (
            <div className="mt-4">
              <EmptyTable icon="chart">
                <div className="text-sm text-gray-600">
                  Nenhuma consulta com falha no período selecionado para montar
                  o gráfico.
                </div>
              </EmptyTable>
            </div>
          ) : (
            <div className="mt-5 bg-white rounded-lg shadow border border-gray-100 p-3">
              <div className="text-sm font-semibold mb-2">
                Falhas por Provedor
              </div>
              <div className="overflow-x-auto">
                <div style={{ width: `${chartWidth}px` }}>
                  <ReactApexChart
                    type="bar"
                    height={360}
                    width={chartWidth}
                    options={chartOptions}
                    series={chartSeries}
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Total de provedores: {providerCounts?.length}
              </div>
            </div>
          )}

          {/* ---------- Tabela ---------- */}
          {isLoadingReportSimplifiedQueries ? (
            <div className="relative h-40 mt-4">
              <DrawerLoading />
            </div>
          ) : errorListReportSimplifiedQueries ? (
            <Alert color="failure" className="mt-4">
              Ocorreu um erro ao carregar as consultas com falha.
            </Alert>
          ) : !listReportSimplifiedQueries ||
            listReportSimplifiedQueries?.length === 0 ? (
            <div className="mt-4">
              <EmptyTable icon="error">
                <div className="text-sm text-gray-600">
                  Nenhuma consulta com falha no período selecionado.
                </div>
              </EmptyTable>
            </div>
          ) : (
            <div className="overflow-x-auto shadow-md rounded-lg mt-4">
              <Table striped style={{ fontSize: "13px" }}>
                <Table.Head>
                  <Table.HeadCell className="bg-red-300">Data</Table.HeadCell>
                  <Table.HeadCell className="bg-red-300">
                    Cliente
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-red-300">
                    Usuário
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-red-300">Insumo</Table.HeadCell>
                  <Table.HeadCell className="bg-red-300">
                    Parâmetro
                  </Table.HeadCell>
                  <Table.HeadCell className="bg-red-300">
                    Provedor
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {listReportSimplifiedQueries.map((item, index) => (
                    <Table.Row
                      key={index}
                      className="bg-white dark:border-gray-700 dark:bg-gray-800"
                    >
                      <Table.Cell className="text-gray-900 dark:text-white py-2">
                        <div className="flex flex-col">
                          <span>
                            {moment(item.createdAt).format("DD/MM/yyyy HH:mm")}
                          </span>
                          <span className="text-xs text-gray-500 italic">
                            {moment(item.createdAt).fromNow()}
                          </span>
                        </div>
                      </Table.Cell>
                      <Table.Cell className="text-gray-900 dark:text-white py-2">
                        {item.socialReason || item.name}
                      </Table.Cell>
                      <Table.Cell className="text-gray-900 dark:text-white py-2">
                        {item.userName}
                      </Table.Cell>
                      <Table.Cell className="text-gray-900 dark:text-white py-2">
                        {item.queryName}
                      </Table.Cell>
                      <Table.Cell className="text-gray-900 dark:text-white py-2">
                        {item.parameter}
                      </Table.Cell>
                      <Table.Cell className="text-gray-900 dark:text-white py-2">
                        {item.providerName}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex overflow-x-auto justify-center mt-3">
              <Pagination
                layout="pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                previousLabel=""
                nextLabel=""
                showIcons
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogErrorsProviders;
